import { Page } from "@mb-pro-ui/components";
import { Grid, Button, useMediaQuery, useTheme, Typography, Box } from "@mui/material";
import { Form, useField } from "react-final-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Switch, useHistory, useParams, useRouteMatch } from "react-router-dom";
import { useSnackbar } from "notistack";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PersonIcon from "@mui/icons-material/Person";
import SimCardIcon from "@mui/icons-material/SimCard";

import { UserDataExtended } from "../utils/types";
import { useApi } from "../utils/ApiProvider";
import useEditedStyles from "../hooks/useEditedStyles";
import { useErrorHandler } from "../hooks/useErrorHandler";

import { usePageSxProps } from "./styles";

import { paths } from "@helios/toolbox-types";
import { SidebarOpenProvider } from "../components/SidebarOpenProvider";
import { SideBarOption } from "@mb-pro-ui/components/SideBarV2";
import { useMenuButtonContainer } from "../components/header/MenuButtonContainerProvider";
import { UserSettingsSubpage } from "./UserSettingsSubpage";
import { UserUnitPermissionsSubpage } from "./UserUnitPermissionsSubpage";
import { APP_NAME } from "../App";
import { ToolboxLabel } from "../components/ToolboxLabel";
type CreateUserRequestBody = paths["/users"]["post"]["requestBody"]["content"]["application/json"];
type CreateUserResponse = paths["/users"]["post"]["responses"]["200"]["content"]["application/json"];

const UserPage = () => {
  const { id } = useParams<{ id: string }>();
  const { singlePageSx } = usePageSxProps();
  const { formatMessage } = useIntl();
  const api = useApi();
  const editedStyle = useEditedStyles();
  const history = useHistory();
  const { closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const { containerRef } = useMenuButtonContainer();
  const { path: basePath } = useRouteMatch<{ userId: string }>();

  const isNew = useMemo(() => id === "new", [id]);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const openSidebar = useCallback(() => setSidebarOpen(true), [setSidebarOpen]);
  const closeSidebar = useCallback(() => setSidebarOpen(false), [setSidebarOpen]);

  // TODO: további kezelt hibák felsorolása!!!
  const { errorHandler: onError } = useErrorHandler<UserDataExtended>([
    [
      { status: 401, type: "user-admin" },
      () => formatMessage({ defaultMessage: "You have no rights for user management." }),
      () => {
        history.push("/");
      },
    ],
    [{ status: 404 }, () => formatMessage({ defaultMessage: "User is not found." }), () => history.push("/users")],
  ]);

  const userQuery = useCallback(() => {
    if (id === "new") {
      const defaultUser: CreateUserRequestBody = {
        name: "",
        fullname: "",
        email: "",
        active: true,
        companies: [],
        rights: {
          firmware_update: false,
          firmware_upload: false,
          remote_files: false,
          global_remote_files: false,
          user_admin: false,
          template_helper: false,
          template_edit: false,
          limited_user: false,
        },
        unit_permissions: null,
      };
      return Promise.resolve(defaultUser as UserDataExtended);
    }
    return api<UserDataExtended>(`/users/${id}`);
  }, [api, id]);

  const saveQuery = useCallback(
    (user: UserDataExtended) => {
      closeSnackbar();
      if (isNew) {
        const { id: _id, ...userData } = user;
        return api(`/users`, { method: "post", body: JSON.stringify(userData) });
      }
      return api(`/users/${id}`, { method: "post", body: JSON.stringify({ ...user, unit_permissions: [] }) });
    },
    [api, closeSnackbar, id, isNew]
  );

  const { mutate: getUserData, data: userData, isLoading, status: userDataQueryStatus } = useMutation({ mutationFn: userQuery, onError });

  const onSaveSuccess = useCallback(
    (response: any) => {
      if (isNew) {
        const { id } = response as CreateUserResponse;
        history.push(`/users/${id}`);
        return;
      }
      getUserData();
    },
    [getUserData, history, isNew]
  );
  const { mutate: saveUser, isLoading: saving } = useMutation({ mutationFn: saveQuery, onSuccess: onSaveSuccess, onError });

  const onSubmit = useCallback(
    async (args: UserDataExtended) => {
      saveUser(args);
    },
    [saveUser]
  );

  useEffect(() => {
    document.title = `${formatMessage({ defaultMessage: "Users" })} - ${APP_NAME}`;
  }, [formatMessage]);

  useEffect(() => {
    const unregister = history.listen(() => {
      closeSnackbar();
    });

    return () => {
      unregister();
    };
  }, [history, closeSnackbar]);

  useEffect(() => {
    getUserData();
  }, [getUserData, id]);

  const userMenuHeader = useMemo(() => <ToolboxLabel />, []);

  const sidebarOptions = useMemo(() => {
    const menuItems: SideBarOption[] = [
      {
        link: "",
        name: <FormattedMessage defaultMessage="Basic information" />,
        icon: <PersonIcon />,
        // classname: basicInfoEdited ? editedStyle.menuItem : "",
      },
      {
        link: "/unit-permissions",
        name: <FormattedMessage defaultMessage="Unit permissions" />,
        icon: <SimCardIcon />,
      },
    ];
    return menuItems;
  }, []);

  const userSubpage = useMemo(() => <UserSettingsSubpage isLoading={isLoading} />, [isLoading]);
  const unitPermissionsSubpage = useMemo(() => <UserUnitPermissionsSubpage userDataQueryStatus={userDataQueryStatus} />, [userDataQueryStatus]);

  return (
    <SidebarOpenProvider open={sidebarOpen}>
      <Page
        sidebarHeader={userMenuHeader}
        sidebarVersion="v1"
        sidebarOptions={sidebarOptions}
        onSidebarOpen={openSidebar}
        onSidebarClose={closeSidebar}
        sidebarVariant={smallScreen ? "temporary" : undefined}
        sidebarDefaultOpen={!smallScreen}
        sidebarMenuButtonContainer={containerRef.current || undefined}
        sx={singlePageSx}
      >
        <Form<UserDataExtended>
          onSubmit={onSubmit}
          initialValues={userData}
          render={({ handleSubmit, dirty }) => (
            <form onSubmit={handleSubmit} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <Grid container spacing={1} sx={{ mb: 1, alignItems: "center" }}>
                {!isNew && (
                  <Grid item>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", ml: 0.5 }}>
                      <UserNameLabel />
                    </Box>
                  </Grid>
                )}
                <Grid item sx={{ flexGrow: 1 }}></Grid>
                <Grid item>
                  <Button color="inherit" startIcon={<SaveOutlinedIcon />} className={dirty ? editedStyle.withHover : ""} disabled={saving} type="submit">
                    {formatMessage({ defaultMessage: "Save" })}
                  </Button>
                </Grid>
              </Grid>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <Switch>
                  <Route exact path={basePath}>
                    {userSubpage}
                  </Route>
                  <Route path={`${basePath}/unit-permissions`}>{unitPermissionsSubpage}</Route>
                </Switch>
              </Box>
            </form>
          )}
        />
      </Page>
    </SidebarOpenProvider>
  );
};

const UserNameLabel = () => {
  const {
    input: { value },
  } = useField("name");
  return (
    <Typography variant="subtitle2" color="secondary.main">
      {value}
    </Typography>
  );
};

export default UserPage;
